import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Input } from './ui/input';

const SOURCE_LABEL = { qr: 'QR code', mobile_button: 'Mobile button', email: 'Welcome email' };
const STATUS_STYLE = {
  redeemed: 'bg-brand-green/10 text-brand-green',
  pending: 'bg-yellow-50 text-yellow-800',
  expired: 'bg-gray-100 text-gray-600',
};

function fmt(dt) { return dt ? new Date(dt).toLocaleString() : '\u2014'; }

export function HandoffsPanel() {
  const { authAxios } = useAuth();
  const [rows, setRows] = useState([]);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async (q) => {
    setLoading(true);
    try {
      const { data } = await authAxios.get('/admin/handoffs', { params: { email: q || undefined, limit: 200 } });
      setRows(data);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load app handoffs.');
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => {
    const t = setTimeout(() => load(email), 300);
    return () => clearTimeout(t);
  }, [email, load]);

  const counts = useMemo(() => rows.reduce((c, r) => ({ ...c, [r.status]: (c[r.status] || 0) + 1 }), {}), [rows]);

  return (
    <div data-testid="admin-handoffs-panel" className="bg-white rounded-2xl border border-brand-cream-dark p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="font-serif text-2xl text-brand-green">App handoffs</h2>
          <p className="text-sm text-brand-green/70 mt-1">One-time web-to-app sign-ins from the QR code, the mobile button, and welcome emails.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-brand-green/50" />
            <Input data-testid="admin-handoffs-search" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Search by email" className="pl-9 w-60" />
          </div>
          <button onClick={() => load(email)} data-testid="admin-handoffs-refresh"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm border border-brand-cream-dark text-brand-green hover:bg-brand-cream">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>
      <div className="flex flex-wrap gap-3 mb-4 text-xs" data-testid="admin-handoffs-counts">
        {['redeemed', 'pending', 'expired'].map((s) => (
          <span key={s} className={`px-2 py-1 rounded-full capitalize ${STATUS_STYLE[s]}`}>{s}: {counts[s] || 0}</span>
        ))}
      </div>
      {error && <p className="text-red-600 text-sm mb-3">{error}</p>}
      {rows.length === 0 && !loading
        ? <p data-testid="admin-handoffs-empty" className="text-sm text-brand-green/60 py-6 text-center">No handoffs yet.</p>
        : <HandoffTable rows={rows} />}
    </div>
  );
}

function HandoffTable({ rows }) {
  return (
    <div className="overflow-x-auto max-h-[480px] overflow-y-auto">
      <table className="min-w-full text-sm" data-testid="admin-handoffs-table">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-brand-green/60">
            {['User', 'Source', 'Status', 'Created', 'Redeemed', 'Device'].map((h) => <th key={h} className="py-2 pr-4">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={`${r.created_at}_${i}`} data-testid="admin-handoffs-row" className="border-t border-brand-cream-dark align-top">
              <td className="py-2 pr-4 text-brand-green break-all">{r.email || r.user_id}</td>
              <td className="py-2 pr-4 text-brand-green/80 whitespace-nowrap">{SOURCE_LABEL[r.source] || r.source}</td>
              <td className="py-2 pr-4"><span className={`px-2 py-0.5 rounded-full text-xs capitalize ${STATUS_STYLE[r.status]}`}>{r.status}</span></td>
              <td className="py-2 pr-4 text-brand-green/80 whitespace-nowrap">{fmt(r.created_at)}</td>
              <td className="py-2 pr-4 text-brand-green/80 whitespace-nowrap">{fmt(r.used_at)}</td>
              <td className="py-2 pr-4 text-brand-green/70 text-xs max-w-xs">
                {r.redeem_ip || '\u2014'}
                {r.redeem_user_agent && <div className="truncate" title={r.redeem_user_agent}>{r.redeem_user_agent}</div>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
